import { z } from 'zod';
import { OperationalAwardTypeCode, StageId } from './awards.js';
import { Cents, PlainDate } from './common.js';
import { ScanStatus } from './documents.js';
import { PageInfo, pageFields } from './paging.js';

const RewardView = z.enum(['awards', 'elections', 'documents']);
export const MyRewardsQuery = z
  .object({ ...pageFields, view: RewardView.default('awards') })
  .strict();
export type MyRewardsQuery = z.input<typeof MyRewardsQuery>;

/** `GET /letters-to-hand-out`: the lines routed to this person to hand out, a page at a time. */
export const LettersToHandOutQuery = z.object({ ...pageFields }).strict();
export type LettersToHandOutQuery = z.input<typeof LettersToHandOutQuery>;

const Timestamp = z.iso.datetime({ offset: true });
const Count = z.number().int().nonnegative().refine(Number.isSafeInteger);
const Amount = Cents.refine((value) => value >= 0);
export const RewardTaxEstimates = z.object({ a: Amount, b: Amount, c: Amount, d: Amount }).strict();

const Award = z
  .object({
    id: z.uuid(),
    displayId: z.string().nullable(),
    type: OperationalAwardTypeCode,
    title: z.string().nullable(),
    awardDate: PlainDate.nullable(),
  })
  .strict();

/** Safe file metadata; no storage paths, upload actors or staff-only document facts. */
export const RewardDocument = z
  .object({
    id: z.uuid(),
    purpose: z.enum(['letter', 'earning_statement']),
    fileName: z.string(),
    contentType: z.string(),
    sizeBytes: Count,
    uploadedAt: Timestamp,
    scanStatus: ScanStatus,
    available: z.boolean(),
    downloadUrl: z.string().startsWith('/api/v1/awards/').nullable(),
  })
  .strict();
export type RewardDocument = z.infer<typeof RewardDocument>;

/** The same own-line projection is used for list rows and the employee detail. */
export const MyReward = z
  .object({
    lineId: z.uuid(),
    version: z.number().int().positive(),
    award: Award,
    stage: StageId.nullable(),
    amounts: z
      .object({ cashCents: Amount, options: Count, totalValueCents: Amount.nullable() })
      .strict(),
    valuation: z
      .object({ fmvCents: Amount.positive().nullable(), fmvEffectiveFrom: PlainDate.nullable() })
      .strict(),
    completion: z
      .object({ closedAt: Timestamp.nullable(), shareworksEnteredAt: Timestamp.nullable() })
      .strict(),
    /** Exact current issued letter; never reconstructed from current policy. */
    vesting: z
      .object({ documentId: z.uuid(), text: z.string().min(1).max(300) })
      .strict()
      .nullable(),
    election: z
      .object({
        timeZone: z.string().nullable(),
        asOf: Timestamp,
        canElect: z.boolean(),
        availability: z.enum([
          'available',
          'submitted',
          'closed_as_cash',
          'expired',
          'not_open',
          'unavailable',
        ]),
        deadlineDate: PlainDate.nullable(),
        deadlineAt: Timestamp.nullable(),
        /** Schedule saved with this award; absent legacy windows make no reminder promise. */
        reminderDates: z.array(PlainDate).nullable().optional(),
        choice: z.enum(['a', 'b', 'c', 'd', 'closed_as_cash']).nullable(),
        stockValueCents: Amount.nullable(),
        shares: Count.nullable(),
        cashCents: Amount.nullable(),
        electedAt: Timestamp.nullable(),
        estimates: z
          .object({ label: z.literal('Estimate'), taxCents: RewardTaxEstimates })
          .strict()
          .nullable(),
      })
      .strict()
      .nullable(),
    payout: z
      .object({
        cashCents: Cents.nullable(),
        taxCents: Cents.nullable(),
        /** Final Payroll count after payment; the original election remains separate. */
        shares: Count.nullable(),
        paidAt: Timestamp.nullable(),
      })
      .strict(),
    documents: z.array(RewardDocument),
  })
  .strict();
export type MyReward = z.infer<typeof MyReward>;
export const MyRewardsSummary = z
  .object({
    scope: z.literal('all_visible_own_rewards'),
    asOf: Timestamp,
    awardCount: Count,
    options: Count,
    /** Null if any paid historical line lacks its cash amount; never a partial total. */
    cashPaidCents: Cents.nullable(),
    paidCount: Count,
    missingPaidCashCount: Count,
  })
  .strict();
export type MyRewardsSummary = z.infer<typeof MyRewardsSummary>;
export const MyRewards = z
  .object({
    items: z.array(MyReward),
    summary: MyRewardsSummary,
    page: PageInfo,
  })
  .strict();
export type MyRewards = z.infer<typeof MyRewards>;

/**
 * `GET /api/v1/my-rewards/election-preview`: the facts "How the choice works" needs before any
 * election opens (E3.empty, APP-093). Today's settings, never an award's: an open election always
 * uses the values saved with its award.
 */
export const MyElectionPreview = z
  .object({
    asOf: Timestamp,
    /** Days from the award date to choose: the PAA election window setting, today. */
    windowDays: z.number().int().min(1),
    /** Reminders, in days before the deadline (0 is the last day): the setting, today. */
    reminderDaysBefore: z.array(z.number().int().nonnegative()),
    /** Today's share price and when it took effect; null until a System Admin sets one. */
    fmv: z
      .object({ cents: Amount.refine((v) => v > 0), effectiveFrom: PlainDate.nullable() })
      .strict()
      .nullable(),
  })
  .strict();
export type MyElectionPreview = z.infer<typeof MyElectionPreview>;

/** Distribution authority follows the saved holder; it does not grant full award access. */
export const LetterToHandOut = z
  .object({
    lineId: z.uuid(),
    version: z.number().int().positive(),
    award: Award,
    recipient: z.object({ id: z.uuid(), name: z.string() }).strict(),
    releasedAt: Timestamp,
    employeeVisible: z.boolean(),
    releasedEarly: z.boolean(),
    actions: z.object({ releaseEarly: z.boolean() }).strict(),
    letters: z.array(RewardDocument.extend({ purpose: z.literal('letter') }).strict()),
  })
  .strict();
export type LetterToHandOut = z.infer<typeof LetterToHandOut>;
export const LettersToHandOut = z
  .object({ items: z.array(LetterToHandOut), page: PageInfo })
  .strict();
export type LettersToHandOut = z.infer<typeof LettersToHandOut>;
